import { ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { hubConst } from '@app/core';
import { enableOrDisableCtrl } from '@app/utils';
import { TranslateService } from '@ngx-translate/core';
import { EditorConteudoComponent } from '@shared/editor-conteudo/editor-conteudo.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

enum ModoVisualizacao {
    Leitura,
    Edicao
}

@Component({
    selector: 'gpe-text-edit-inline',
    templateUrl: './text-edit-inline.component.html',
    styleUrls: ['./text-edit-inline.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => TextEditInlineComponent),
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextEditInlineComponent implements OnInit, OnDestroy, ControlValueAccessor {

    @Input() public paramsResource = {};
    @Input() public inputPlaceholder = '';
    @Input() public full = false;
    @Input() public editorHeight: number;
    @Input() public resizeEnabled: boolean = true;
    @ViewChild(EditorConteudoComponent, { static: false }) ckeditor: EditorConteudoComponent;

    public readonly resourceEditor = `${hubConst.api.avaliacoes_api}upload/ckeditor-image`;

    public editorConfig: object;
    public isLeitura$: Observable<boolean>;
    public editorCtrl = new FormControl();

    private _momento$ = new BehaviorSubject(ModoVisualizacao.Leitura);
    private _change = (_: any) => {};
    private _touched = () => {};
    private _container: HTMLElement;
    private _historico: string;

    constructor(
        private render: Renderer2,
        private _translate: TranslateService
    ) {
        this.isLeitura$ = this._momento$.asObservable().pipe(map(res => res === ModoVisualizacao.Leitura));
    }

    public ngOnInit(): void {
        this.editorConfig = {
            resize_enabled: this.resizeEnabled
        };
    }

    public ngOnDestroy(): void {
        this.onTrocarParaLeitura();
    }

    public get isLeitura(): boolean {
        return this._momento$.value === ModoVisualizacao.Leitura;
    }

    public get isEdicao(): boolean {
        return this._momento$.value === ModoVisualizacao.Edicao;
    }

    public get preVisualizacao(): string {
        return !!this.editorCtrl.value ? this.editorCtrl.value : this.placeholder();
    }

    public onTrocarParaEdicao(): void {
        if (!this.editorCtrl.disabled) {
            this._touched();
            this._historico = this.editorCtrl.value;
            this._momento$.next(ModoVisualizacao.Edicao);
            this._container = document.createElement('div');
            this.render.addClass(this._container, 'modal-background');
            this.render.listen(this._container, 'click', this.onTrocarParaLeitura);
            document.body.appendChild(this._container);
        }
    }

    public onReady(): void {
        this.ckeditor.onFocus();
    }

    public onBlur(value: any): void {
        this.editorCtrl.setValue(value);
        this.onTrocarParaLeitura();
    }

    public onTrocarParaLeitura = (alterar = true): void => {
        setTimeout(() => {
            this._touched();
            if (alterar) {
                this._change(!!this.editorCtrl.value ? this.editorCtrl.value : null);
            } else {
                this.editorCtrl.setValue(this._historico);
            }
            this._momento$.next(ModoVisualizacao.Leitura);
            if (this._container) {
                document.body.removeChild(this._container);
                this._container = null;
            }
        }, 150);
    }

    public writeValue(obj: any): void {
        this.editorCtrl.setValue(obj);
    }

    public registerOnChange(fn: (v: any) => void): void {
        this._change = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this._touched = fn;
    }

    public setDisabledState?(isDisabled: boolean): void {
        if (isDisabled) {
            this.onTrocarParaLeitura();
        }
        enableOrDisableCtrl(!isDisabled, this.editorCtrl);
    }

    private placeholder(): string {
        const element = document.createElement('span');
        element.setAttribute("class", "inline__placeholder font-italic");
        element.textContent = this.inputPlaceholder || this._translate.instant('geral.escreva-aqui');
        return element.outerHTML;
    }
}
